import React from "react";
import LegalShell, { LegalDocument, LegalSection } from "@/components/site/LegalShell";

const EFFECTIVE = "January 2026";

export default function PrivacyPolicy() {
    return (
        <LegalShell>
            <LegalDocument
                eyebrow="Privacy"
                title="Privacy Policy"
                effective={EFFECTIVE}
            >
                <p className="text-zinc-600">
                    YugOS is committed to protecting the privacy of the people and businesses
                    that use it. This policy explains what information we collect, why we
                    collect it, and how it is safeguarded.
                </p>

                <LegalSection title="1. Information we collect">
                    <p>We collect only the information necessary to operate the product:</p>
                    <ul className="list-disc pl-5 space-y-1.5">
                        <li>
                            <strong className="text-zinc-900">Account details</strong> — your
                            name, work email, hashed password and assigned role.
                        </li>
                        <li>
                            <strong className="text-zinc-900">Session data</strong> — IP
                            address and basic browser / device information at the time of
                            login, used to secure your account.
                        </li>
                        <li>
                            <strong className="text-zinc-900">Product usage</strong> — actions
                            performed inside YugOS (sales orders, invoices, purchase orders,
                            inventory movements) and internal audit logs tied to your account.
                        </li>
                        <li>
                            <strong className="text-zinc-900">Anonymous analytics</strong> —
                            aggregate signals used to improve reliability and performance.
                        </li>
                    </ul>
                </LegalSection>

                <LegalSection title="2. Why we collect it">
                    <ul className="list-disc pl-5 space-y-1.5">
                        <li>To authenticate you and secure your account.</li>
                        <li>To provide the requested product functionality.</li>
                        <li>To detect and prevent abuse (brute-force, unusual API traffic).</li>
                        <li>To improve the product based on aggregate usage.</li>
                    </ul>
                    <p>
                        We do not sell personal information and we do not use your business
                        data to train third-party AI models.
                    </p>
                </LegalSection>

                <LegalSection title="3. Data storage and protection">
                    <ul className="list-disc pl-5 space-y-1.5">
                        <li>Passwords are hashed with bcrypt; plaintext is never stored.</li>
                        <li>API traffic is served over HTTPS in production.</li>
                        <li>
                            Authorization is enforced server-side on every request via JWT
                            bearer tokens and role-based access control.
                        </li>
                        <li>
                            Rate limits protect authentication and AI endpoints from
                            automated abuse.
                        </li>
                    </ul>
                </LegalSection>

                <LegalSection title="4. Third-party services">
                    <p>
                        YugOS relies on a small number of trusted providers to deliver the
                        product:
                    </p>
                    <ul className="list-disc pl-5 space-y-1.5">
                        <li>
                            <strong className="text-zinc-900">Cloud infrastructure</strong> —
                            application hosting, database storage.
                        </li>
                        <li>
                            <strong className="text-zinc-900">AI model provider</strong> —
                            used exclusively to power Yug Brain. Only the specific question
                            you ask and a summarised business snapshot are sent; no
                            credentials or personal identifiers.
                        </li>
                    </ul>
                </LegalSection>

                <LegalSection title="5. Cookies and local storage">
                    <p>
                        YugOS uses browser local storage to remember your session token so
                        you stay signed in between page loads. We do not use tracking cookies,
                        advertising pixels, or cross-site trackers.
                    </p>
                </LegalSection>

                <LegalSection title="6. Your rights">
                    <p>You may, at any time:</p>
                    <ul className="list-disc pl-5 space-y-1.5">
                        <li>Request a copy of the personal data we hold about you.</li>
                        <li>Ask us to correct information that is inaccurate.</li>
                        <li>Ask us to delete your account and associated personal data.</li>
                        <li>Withdraw consent for optional processing.</li>
                    </ul>
                    <p>
                        Send any of the above requests to{" "}
                        <a
                            href="mailto:privacy@yugos.app"
                            className="text-zinc-950 underline underline-offset-2 hover:text-blue-600"
                        >
                            privacy@yugos.app
                        </a>
                        .
                    </p>
                </LegalSection>

                <LegalSection title="7. Data retention">
                    <p>
                        We keep your data for as long as your account is active or as needed
                        to provide the service. When an account is deleted, personal data is
                        removed within 30 days, except records we are legally required to
                        retain (for example, financial audit trails).
                    </p>
                </LegalSection>

                <LegalSection title="8. Contact">
                    <p>
                        Questions about this policy or a request regarding your data can be
                        sent to{" "}
                        <a
                            href="mailto:privacy@yugos.app"
                            className="text-zinc-950 underline underline-offset-2 hover:text-blue-600"
                        >
                            privacy@yugos.app
                        </a>
                        .
                    </p>
                </LegalSection>
            </LegalDocument>
        </LegalShell>
    );
}
